import * as React from "react";
import { Row, Col, Skeleton, Button, Tooltip } from "antd";
import { EditableDetailItem } from "./EditableDetailItem";
import { DetailsInfo } from "./DetailsInfo";
import { fileDetailsStore } from "../FileDetailsStore";
import { WithTranslation, withTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import {
  DocumentsTableRowActions,
  DocumentsTableRowActionsOpts,
} from "Pages/Project/Documents/Components/DocumentsTableRowActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { EllipsisLeft } from "Components/General/EllipsisLeft";
import { userInfoStore } from "Stores";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { WOPIViewerParams, wopiViewerStore } from "Stores/WOPIViewerStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { copyToClipboard } from "Components/Utils/CopyToClipboard";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Components/FilePrint/utils/modalProps";
import { Preview } from "Pages/Project/Investigations/Components/Preview";
import { formatUsername } from "utils/formatUsername";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

interface DetailsProps extends WithTranslation, WithModal {}

export const Details = withTranslation(I18N_NS.PROJECT)(
  withModal(
    observer(
      class extends React.Component<DetailsProps> {
        readonly state: { needSave: boolean } = { needSave: false };

        componentWillUnmount() {
          if (this.state.needSave) {
            documentsTableStore.refresh();
          }
        }

        get otherOwnerForRestrictedUser() {
          if (!userInfoStore.allowedToChangeSetting) {
            // if user - is restricted user
            if (fileDetailsStore.data.ownerId !== userInfoStore.id) {
              return this.props.t("Other user");
            } else {
              return userInfoStore.userID;
            }
          }
          return undefined;
        }

        get viewers() {
          const data = fileDetailsStore.data;

          if (data.viewers) {
            if (data.viewers.webViewer) {
              if (data.state === "EXTERN_SYMLINK") {
                return apryseViewerStore.fetch(data.id, {
                  fileName: data.name,
                  canDownload: false, //externalFile.download || data.fileActions.downloadWithWatermark,
                  isExternalFile: true,
                  linkedFolderId: data.linkId,
                });
              } else {
                return apryseViewerStore.fetch(data.id, {
                  fileName: data.name,
                  canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
                });
              }
            }
            if (data.viewers.easyViewer) {
              if (data.state === "EXTERN_SYMLINK") {
                return pdfViewerStore.fetch(data.id, {
                  fileName: data.name,
                  canDownload: false, //externalFile.download || data.fileActions.downloadWithWatermark,
                  isExternalFile: true,
                  linkedFolderId: data.linkId,
                });
              } else {
                return pdfViewerStore.fetch(data.id, {
                  fileName: data.name,
                  canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
                });
              }
            }
            if (data.viewers.office365) {
              const params: WOPIViewerParams = {
                fileName: data.name,
              };
              if (data.state === "EXTERN_SYMLINK") {
                params.linkId = data.linkId;
              }
              return wopiViewerStore.fetch(data.id, params);
            }
            if (data.viewers.onlineViewer) {
              return onlineViewerStore.fetch(data.id, { fileName: data.name });
            }
            if (data.viewers.bcfViewer) {
              this.props.showModal(BCFViewerModal, {
                modalProps: {
                  ...modalProps,
                  maskClosable: true,
                  width: "100%",
                  title: `${this.props.t("File")}: ${data.name}`,
                  style: { maxWidth: "1300px" },
                  className: "full-height-modal with-drawer bcf-modal",
                  zIndex: 1013,
                },
                componentProps: { fileId: data.id },
              });
            }
          }
          return null;
        }

        get viewersTooltip() {
          const { t } = this.props;
          const data = fileDetailsStore.data;

          if (data.viewers) {
            if (data.viewers.webViewer) {
              return t("View with WebViewer");
            }
            if (data.viewers.easyViewer) {
              return t("View with Easy Viewer");
            }
            if (data.viewers.office365) {
              return t("View with Office Online");
            }
            if (data.viewers.onlineViewer) {
              return t("View with Online Viewer");
            }
          }
          return null;
        }

        get documentsRowActionsProps() {
          let documentsRowActionsProps: DocumentsTableRowActionsOpts = { onRefresh: fileDetailsStore.refresh };
          if (fileDetailsStore.from === "dashboard") {
            documentsRowActionsProps = Object.assign(documentsRowActionsProps, {
              showBasicFileActions: true,
              showFolderActions: false,
              showModuleFileActions: false,
              showOneFileActions: true,
              allowedKeys: ["jump_to_dir"],
              simpleDownload: true,
            });
          }
          return documentsRowActionsProps;
        }

        render() {
          const { t } = this.props;
          const isViewerActive =
            fileDetailsStore.data &&
            (fileDetailsStore.data.viewers?.easyViewer ||
              fileDetailsStore.data.viewers?.office365 ||
              fileDetailsStore.data.viewers?.onlineViewer ||
              fileDetailsStore.data.viewers?.bcfViewer);

          return (
            <Skeleton loading={fileDetailsStore.state === "LOADING"}>
              <Row gutter={15}>
                <Col lg={18} md={14}>
                  <EditableDetailItem
                    editable={false}
                    label={t("Filepath")}
                    value={fileDetailsStore.data.filepath}
                    render={(path) => (
                      <div onClick={() => copyToClipboard(path)} style={{ backgroundColor: "#fff", cursor: "pointer" }}>
                        <EllipsisLeft text={path} maxWidth={600} />
                      </div>
                    )}
                  />
                  <EditableDetailItem
                    editable={
                      (fileDetailsStore.from === "project" || fileDetailsStore.from === "documents") &&
                      fileDetailsStore.data.fileActions &&
                      fileDetailsStore.data.fileActions.rename
                    }
                    label={t("Filename")}
                    value={fileDetailsStore.data.name}
                    validateFilename
                    onSave={(name) => {
                      this.setState({ needSave: true });
                      return fileDetailsStore.editFileDetails({ name });
                    }}
                  />
                  <EditableDetailItem
                    editable={
                      (fileDetailsStore.from === "project" || fileDetailsStore.from === "documents") &&
                      fileDetailsStore.data.fileActions &&
                      fileDetailsStore.data.fileActions.updateDescription
                    }
                    label={t("Description")}
                    value={fileDetailsStore.data.description}
                    onSave={(description) => {
                      this.setState({ needSave: true });
                      return fileDetailsStore.editFileDetails({ description });
                    }}
                  />
                  <EditableDetailItem
                    editable={
                      (fileDetailsStore.from === "project" || fileDetailsStore.from === "documents") &&
                      fileDetailsStore.data.fileActions &&
                      fileDetailsStore.data.fileActions.updateIndex
                    }
                    label={"Index/Version"}
                    value={fileDetailsStore.data.index}
                    onSave={(index) => {
                      this.setState({ needSave: true });
                      return fileDetailsStore.editFileDetails({ index });
                    }}
                  />
                  <DetailsInfo
                    fileOwner={formatUsername({
                      firstName: fileDetailsStore.data.ownerFirstName,
                      surname: fileDetailsStore.data.ownerSurname,
                    })}
                    size={fileDetailsStore.data.size}
                    lastChange={+fileDetailsStore.data.created}
                    guid={fileDetailsStore.data.guid}
                    md5={fileDetailsStore.data.checksumMd5}
                    sha={fileDetailsStore.data.checksumSha}
                  />
                </Col>
                <Col lg={6} md={10}>
                  <Row justify="center">
                    <Tooltip placement="top" title={this.viewersTooltip}>
                      <Col
                        style={isViewerActive ? { cursor: "pointer" } : null}
                        md={{ span: 24, order: 0 }}
                        sm={{ span: 12, order: 1 }}
                        xs={{ span: 24, order: 1 }}
                        onClick={() => this.viewers}
                        className="mb-2"
                      >
                        <Preview fileId={fileDetailsStore.data.id} hasPreview={fileDetailsStore.data.preview} />
                      </Col>
                    </Tooltip>
                    <Col md={{ span: 24, order: 1 }} sm={{ span: 24, order: 0 }} xs={{ span: 24, order: 0 }}>
                      <DocumentsTableRowActions data={fileDetailsStore.data} {...this.documentsRowActionsProps}>
                        <Button style={{ width: "100%", marginBottom: 5 }} type="primary">
                          {t("Actions")}
                          <FontAwesomeIcon icon={["far", "chevron-down"]} style={{ marginLeft: 5 }} fixedWidth={true} />
                        </Button>
                      </DocumentsTableRowActions>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Skeleton>
          );
        }
      }
    )
  )
);
